<template>
  <div class="dp-root" :style="{ '--dp-accent': color }">
    <div v-if="showPresets" class="dp-presets">
      <button class="dp-chip" @click="applyPreset('today')">Today</button>
      <button class="dp-chip" @click="applyPreset('yesterday')" v-if="type==='date'">Yesterday</button>
      <button class="dp-chip" @click="applyPreset('last7')" v-if="type==='date'">Last 7 days</button>
      <button class="dp-chip" @click="applyPreset('thisMonth')">This month</button>
      <button class="dp-chip" @click="applyPreset('lastMonth')">Last month</button>
      <div class="dp-spacer"></div>
      <button class="dp-btn dp-btn--ghost" @click="clear">Очистить</button>
    </div>
    <div class="dp-header">
      <button class="dp-nav" @click="prev"><span aria-hidden>
        <Icon type="chevron_left" :size="24" color="var(--button-icon-tint)" />
      </span></button>
      <div class="dp-title">
        <span v-if="type==='date'">{{ monthLabel(viewYear, viewMonth) }}</span>
        <span v-else>{{ viewYear }}</span>
      </div>
      <button class="dp-nav" @click="next"><span aria-hidden>
        <Icon type="chevron_right" :size="24" color="var(--button-icon-tint)" />
      </span></button>
    </div>
    <div v-if="type==='date'" class="dp-grid">
      <div class="dp-weekday" v-for="wd in weekdays" :key="wd">{{ wd }}</div>

      <button
          v-for="cell in monthCells"
          :key="cell.iso + '-' + cell.inCurrentMonth"
          class="dp-cell"
          :class="{
          'is-out': !cell.inCurrentMonth,
          'is-disabled': cell.disabled,
          'is-weekend': isWeekend(cell.iso),
          'has-event': hasEvent(cell.iso)
        }"
          :disabled="cell.disabled"
          @click="selectDate(cell.iso)"
          type="button"
      >
        <span
            class="dp-num"
            :class="{
               'dp-day-selected': isSelectedDate(cell.iso),
               'db-day-today': isToday(cell.iso),
            }"
        >{{ cell.day }}
        </span>
        <span class="dp-dot" v-if="hasEvent(cell.iso)"></span>
      </button>
    </div>
    <div v-else class="dp-grid dp-grid--months">
      <button
          v-for="m in 12"
          :key="m"
          class="dp-cell dp-cell--month"
          :class="{
          'is-selected': isSelectedMonth(viewYear, m),
          'is-disabled': isMonthDisabled(viewYear, m),
          'is-today': isCurrentMonth(viewYear, m) && !isSelectedMonth(viewYear, m)
        }"
          :disabled="isMonthDisabled(viewYear, m)"
          @click="selectMonth(viewYear, m)"
          type="button"
      >
        <span class="dp-month-label">{{ shortMonthLabel(viewYear, m) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppDatePickerPure',
  props: {
    value: { type: [String, null], default: null },
    type: { type: String, default: 'date' },
    color: { type: String, default: '#5E81F4' },
    min: { type: String, default: null },
    max: { type: String, default: null },
    firstDayOfWeek: { type: Number, default: 1 },
    locale: { type: String, default: 'uk-UA' },
    autoApply: { type: Boolean, default: true },
    showPresets: { type: Boolean, default: true },
    markedDates: { type: Array, default: () => [] },
    disabledWeekdays: { type: Array, default: () => [] }
  },
  data() {
    const now = new Date()
    return {
      inner: this.value || null,
      viewYear: this.value ? this.parseInitYear(this.value) : now.getFullYear(),
      viewMonth: this.value ? this.parseInitMonth(this.value) : (now.getMonth() + 1)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.inner = v
        if (v) {
          this.viewYear = this.parseInitYear(v)
          this.viewMonth = this.parseInitMonth(v)
        }
      }
    }
  },
  computed: {
    currentYear() { return new Date().getFullYear() },
    currentMonth() { return new Date().getMonth() + 1 },
    weekdays() {
      const base = new Date(2023, 0, 1)
      const names = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(base)
        d.setDate(base.getDate() + i)
        return d.toLocaleDateString(this.locale, { weekday: 'short' })
            .replace('.', '')
            .slice(0, 3)
      })
      const shift = (arr, n) => arr.slice(n).concat(arr.slice(0, n))
      return this.firstDayOfWeek === 1 ? shift(names, 1) : names
    },
    monthCells() {

      const year = this.viewYear
      const month = this.viewMonth

      const firstOfMonth = new Date(year, month - 1, 1)
      let startOffset = firstOfMonth.getDay()
      if (this.firstDayOfWeek === 1) startOffset = (startOffset + 6) % 7

      const daysInPrev = new Date(year, month - 1, 0).getDate()
      const daysInCur = new Date(year, month, 0).getDate()

      const cells = []

      for (let i = startOffset - 1; i >= 0; i--) {
        const day = daysInPrev - i
        const d = new Date(year, month - 2, day)
        const iso = this.toISO(d)
        cells.push(this.makeCell(iso, day, false))
      }
      for (let day = 1; day <= daysInCur; day++) {
        const d = new Date(year, month - 1, day)
        const iso = this.toISO(d)
        cells.push(this.makeCell(iso, day, true))
      }
      while (cells.length < 42) {
        const last = cells[cells.length - 1]
        const next = this.addDays(last.iso, 1)
        const d = this.parseISO(next)
        cells.push(this.makeCell(next, d.getDate(), false))
      }
      return cells
    },
    todayISO() {
      const t = new Date()
      return this.toISO(new Date(t.getFullYear(), t.getMonth(), t.getDate()))
    }
  },
  methods: {
    isCurrentMonth(y, m) {
      return y === this.currentYear && m === this.currentMonth
    },
    pad(n) { return n < 10 ? `0${n}` : `${n}` },
    toISO(d) { return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}` },
    parseISO(iso) { return new Date(`${iso}T00:00:00`) },
    parseInitYear(val) {
      if (this.type === 'month') return Number(val.split('-')[0])
      return Number(val.split('-')[0])
    },
    parseInitMonth(val) {
      if (this.type === 'month') return Number(val.split('-')[1])
      return Number(val.split('-')[1])
    },
    addDays(iso, n) {
      const d = this.parseISO(iso)
      d.setDate(d.getDate() + n)
      return this.toISO(d)
    },


    makeCell(iso, day, inCurrentMonth) {
      return { iso, day, inCurrentMonth, disabled: !this.allowedDate(iso) }
    },
    allowedDate(iso) {

      const d = this.parseISO(iso)
      const wd = d.getDay()
      if (this.disabledWeekdays.includes(wd)) return false
      if (this.min && iso < this.min) return false
      if (this.max && iso > this.max) return false
      return true
    },
    hasEvent(iso) { return this.markedDates.includes(iso) },
    isToday(iso) { return iso === this.todayISO },
    isWeekend(iso) {
      const wd = this.parseISO(iso).getDay()
      return wd === 0 || wd === 6
    },
    isSelectedDate(iso) { return this.type === 'date' && this.inner === iso },
    isSelectedMonth(y, m) {
      return this.type === 'month' && this.inner === `${y}-${this.pad(m)}`
    },
    isMonthDisabled(y, m) {
      const iso = `${y}-${this.pad(m)}-01`
      if (this.min && iso < this.min) return true
      if (this.max && iso > this.max) return true
      return false
    },

    selectDate(iso) {
      if (!this.allowedDate(iso)) return
      this.inner = iso
      this.$emit('input', iso)
      if (this.autoApply) this.$emit('apply', iso)
    },
    selectMonth(y, m) {
      const val = `${y}-${this.pad(m)}`
      if (this.isMonthDisabled(y, m)) return
      this.inner = val
      this.$emit('input', val)
      if (this.autoApply) this.$emit('apply', val)
    },
    clear() {
      this.inner = null
      this.$emit('input', null)
      this.$emit('clear')
    },
    prev() {
      if (this.type === 'date') {
        let y = this.viewYear, m = this.viewMonth - 1
        if (m < 1) { m = 12; y-- }
        this.viewYear = y; this.viewMonth = m
      } else {
        this.viewYear -= 1
      }
    },
    next() {
      if (this.type === 'date') {
        let y = this.viewYear, m = this.viewMonth + 1
        if (m > 12) { m = 1; y++ }
        this.viewYear = y; this.viewMonth = m
      } else {
        this.viewYear += 1
      }
    },
    monthLabel(y, m) {
      const d = new Date(y, m - 1, 1)
      return d.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' })
    },
    shortMonthLabel(y, m) {
      const d = new Date(y, m - 1, 1)
      return d.toLocaleDateString(this.locale, { month: 'short' })
    },
    applyPreset(name) {
      const now = new Date()
      const y = now.getFullYear()
      const m = this.pad(now.getMonth() + 1)
      const d = this.pad(now.getDate())

      if (this.type === 'month') {
        if (name === 'thisMonth') return this.selectMonth(y, Number(m))
        if (name === 'lastMonth') {
          const lm = new Date(y, now.getMonth() - 1, 1)
          return this.selectMonth(lm.getFullYear(), lm.getMonth() + 1)
        }
        return this.selectMonth(y, Number(m))
      }

      switch (name) {
        case 'today': return this.selectDate(`${y}-${m}-${d}`)
        case 'yesterday': {
          const yy = new Date(y, now.getMonth(), now.getDate() - 1)
          return this.selectDate(this.toISO(yy))
        }
        case 'last7': {
          return this.selectDate(`${y}-${m}-${d}`)
        }
        case 'thisMonth': return this.selectDate(`${y}-${m}-01`)
        case 'lastMonth': {
          const lm = new Date(y, now.getMonth() - 1, 1)
          return this.selectDate(this.toISO(lm))
        }
      }
    }
  }
}
</script>

<style scoped>

.dp-root {
  --dp-accent: var(--calendar_point_tint);
  font-family: 'Inter', sans-serif;
  user-select: none;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 8px;
}

.dp-presets {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.dp-chip {
  height: 28px;
  padding: 0 10px;
  border-radius: 16px;
  border: 1px solid #e2e2e2;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.dp-chip:hover {
  background: #f5f5f7;
}
.dp-spacer {
  flex: 1;
}
.dp-btn {
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
}
.dp-btn--ghost {
  border-color: #e2e2e2;
  background: #fff;
}
.dp-header {
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  padding: 4px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e2e2e2;
}
.dp-nav {
  width: 32px;
  height: 32px;
  background: #fff;
  cursor: pointer;
}
.dp-title {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  color: #4a4a4a;
}
.dp-day-selected{
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: var(--calendar_point_tint);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
}
.db-day-today{
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border:1px solid var(--calendar_point_tint);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
}

.dp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.dp-grid--months {
  grid-template-columns: repeat(3, 1fr);
}
.dp-weekday {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  padding: 4px 0;
}
.dp-cell {
  position: relative;
  height: 36px;
  border-radius: 4px;
  background: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;
  color: #4a4a4a;
  transition: background .12s ease, border-color .12s ease;
}
.dp-cell:hover {
  background: #f3f3f6;
}
.dp-cell.is-out {
  color: #b7b7b7;
}
.dp-cell.is-disabled {
  opacity: .4;
  cursor: not-allowed;
}
.dp-cell.is-selected {
  background: var(--dp-accent);
  color: #fff;
  border-color: var(--dp-accent);
}
.dp-cell.is-today {
  box-shadow: 0 0 0 2px
  var(--dp-accent) inset;
}
.dp-cell.is-weekend:not(.is-selected) {
  background: #fafafa;
}

.dp-num {
  font-size: 14px;
  line-height: 1;
}
.dp-dot {
  position: absolute;
  bottom: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--dp-accent);
}
.dp-cell--month {
  height: 33px;
}
</style>
